// src/components/DataSourceFilter.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  SpaceBetween,
  Container,
  Header,
  Badge,
  FormField,
  Alert,
  ExpandableSection,
  Checkbox,
  Button,
} from '@cloudscape-design/components';

import { chatAiApi } from '../../services/ChatAiApi';
import './DataSourceFilter.css';

// Constants defined within the same file



// Removed TIME_FILTER_OPTIONS - no longer needed


const COMPETITOR_INFO_CATEGORIES = [
  { id: 'feature_announcements', label: 'Feature Announcements' },
  { id: 'blogs', label: 'Blogs' },
  { id: 'customer_sentiment', label: 'Customer Sentiment' },
];

const DEFAULT_DATA_SOURCES = [
  {
    id: 'highspot',
    dataSourceId: 'HOGW27SKHH',
    name: 'Highspot',
    description: 'Documentation and sales materials from Highspot',
    status: 'ACTIVE',
    type: 'Document',
    category: 'CONTENT_CREATION'
  },
  {
    id: 'css',
    dataSourceId: 'KFXJNCIJF9',
    name: 'CSS',
    description: 'Content and documentation from CSS',
    status: 'ACTIVE',
    type: 'Document',
    category: 'CSS_PREPARATION'
  },
  {
    id: 'marshall',
    dataSourceId: '8ZOR1H8PUH',
    name: 'Marshall',
    description: 'Marshall documentation and resources',
    status: 'ACTIVE',
    type: 'Document',
    category: 'COMPETE_TRENDS'
  },
  {
    id: 'salesforce',
    dataSourceId: 'SFDC123',
    name: 'Salesforce',
    description: 'Opportunity and competitor information from Salesforce',
    status: 'ACTIVE',
    type: 'CRM',
    category: 'COMPETE_TRENDS'
  },
  {
    id: 'azure_competitor_info',
    dataSourceId: 'CMPINFO789',
    name: 'Competitor Information: Azure',
    description: 'Detailed competitor information including feature announcements, blogs, and customer sentiment',
    status: 'ACTIVE',
    type: 'Composite',
    category: 'COMPETE_TRENDS',
    subCategories: COMPETITOR_INFO_CATEGORIES
  },
  {
    id: 'linkedin',
    dataSourceId: 'LNKD789',
    name: 'LinkedIn',
    description: 'Professional networking and company information',
    status: 'ACTIVE',
    type: 'Social',
    category: 'CSS_PREPARATION'
  }
];

// This function has been moved inline to the renderSourceItem function

const DataSourceFilter = ({ onFilterChange, selectedSources = [] }) => {
  const [dataSources, setDataSources] = useState(DEFAULT_DATA_SOURCES);
  const [error, setError] = useState(null);
  const [selectedCompetitorCategories, setSelectedCompetitorCategories] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Debug logging
  useEffect(() => {
    console.log('🔍 DataSourceFilter mounted, data sources:', dataSources.length);
    console.log('🔍 Selected sources:', selectedSources.length);
  }, [dataSources, selectedSources]);

  useEffect(() => {
    fetchDataSources();
  }, []);





const fetchDataSources = async (showLoading = false) => {
  try {
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);
    
    // 🚨 TEMPORARY: Skip auth check during testing - REMOVE AFTER TESTING
    console.log('🚨 Skipping auth check for data sources during testing');
    
    // Check if user is authenticated before making API call (commented out for testing)
    /*
    const getCookie = (name) => {
      const cookieName = name + "=";
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.indexOf(cookieName) === 0) {
          return cookie.substring(cookieName.length, cookie.length);
        }
      }
      return "";
    };
    
    const accessToken = getCookie('accessToken');
    if (!accessToken) {
      console.log('🔐 No access token found, using default data sources');
      setDataSources(DEFAULT_DATA_SOURCES);
      return;
    }
    */
    
    const data = await chatAiApi.getDataSources();
    
    if (data?.dataSources && Array.isArray(data.dataSources)) {
      // Map Bedrock knowledge base data sources to component format
      const mappedSources = data.dataSources.map(source => ({
        id: source.id || source.dataSourceId,
        dataSourceId: source.dataSourceId,
        knowledgeBaseId: source.knowledgeBaseId,
        knowledgeBaseName: source.knowledgeBaseName,
        name: source.name || 'Unnamed Source',
        description: source.description || 'No description available',
        status: source.status || 'ACTIVE',
        type: source.type || 'Document',
        category: source.category || 'DEFAULT',

        lastUpdated: source.lastUpdated,
        createdAt: source.createdAt,
        lastSynced: source.lastSynced
      }));
      
      setDataSources(mappedSources);
      setLastRefresh(new Date());
      
      // Only log when data comes from Bedrock (not cached)
      if (!data.cached && data.knowledgeBasesCount) {
        console.log(`🔄 Loaded ${mappedSources.length} data sources from ${data.knowledgeBasesCount} knowledge bases`);
      }
    } else {
      console.log('📋 Using default data sources');
      setDataSources(DEFAULT_DATA_SOURCES);
    }
  } catch (error) {
    console.error('Error fetching data sources:', error);
    
    if (error.name === 'CORSError') {
      setError('API configuration issue: The server needs to be updated to allow requests from this domain. Using default data sources for now.');
    } else {
      setError('Unable to load data sources from Bedrock. Using default configuration.');
    }
    
    setDataSources(DEFAULT_DATA_SOURCES);
  } finally {
    if (showLoading) {
      setIsLoading(false);
    }
  }
};

const handleRefresh = () => {
  fetchDataSources(true);
};

  const handleSourceToggle = (sourceId) => {
    const source = dataSources.find(ds => ds.dataSourceId === sourceId);
    const isCurrentlySelected = selectedSources.some(s => 
      typeof s === 'string' ? s === sourceId : s.dataSourceId === sourceId
    );
    
    let newSelection;
    if (isCurrentlySelected) {
      // Remove the source
      newSelection = selectedSources.filter(s => 
        typeof s === 'string' ? s !== sourceId : s.dataSourceId !== sourceId
      );
      console.log('🔴 Removed:', source?.name || sourceId);
    } else {
      // Add the source object (not just the ID)
      newSelection = [...selectedSources.filter(s => 
        typeof s === 'string' ? s !== sourceId : s.dataSourceId !== sourceId
      ), source];
      console.log('🟢 Added:', source?.name || sourceId);
    }
    
    onFilterChange(newSelection);
  };



  const handleCompetitorCategoryToggle = (categoryId) => {
    const newSelection = selectedCompetitorCategories.includes(categoryId)
      ? selectedCompetitorCategories.filter(id => id !== categoryId)
      : [...selectedCompetitorCategories, categoryId];
    
    setSelectedCompetitorCategories(newSelection);
    // Keep the current data source selections
    onFilterChange(selectedSources);
  };

  // Removed time filter handlers - no longer needed

  const renderSourceItem = (source) => {
    const isSelected = selectedSources.some(s => 
      typeof s === 'string' ? s === source.dataSourceId : s.dataSourceId === source.dataSourceId
    );
    
    const handleItemClick = (e) => {
      // Only handle clicks if not clicking on expandable sections or other interactive elements
      if (!e.target.closest('.awsui-expandable-section')) {
        handleSourceToggle(source.dataSourceId);
      }
    };
    
    // Get a clean, readable display name
    const getCleanDisplayName = (source) => {
      // Special handling for known problematic names
      if (source.name === 'Transcript Data Source') {
        return 'Competitive Transcripts';
      }
      
      // Use description if it's more readable than the technical name
      if (source.description && 
          source.description !== source.name && 
          source.description.length < 60 &&
          !source.description.startsWith('Data source from') &&
          !source.description.includes('knowledge base')) {
        return source.description;
      }
      
      // Clean up technical names for better readability
      let displayName = source.name;
      
      // Remove common prefixes/suffixes that make names less readable
      displayName = displayName
        .replace(/^knowledge-base-/, '')
        .replace(/-data-source$/, '')
        .replace(/-knowledge-base.*$/, '')
        .replace(/^ci-/, '')
        .replace(/-[a-z0-9]{5}$/, '') // Remove random suffixes like -tnh8v
        .replace(/_[a-z0-9]{5}$/, '') // Remove random suffixes like _gu8ow
        .replace(/-[a-z0-9]{5}-data-source$/, ''); // Remove patterns like -dztzk-data-source
      
      // Convert kebab-case and snake_case to Title Case
      displayName = displayName
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      
      return displayName;
    };
    
    const displayName = getCleanDisplayName(source);
    
    return (
      <div 
        key={source.dataSourceId} 
        className={`data-source-item ${isSelected ? 'selected' : ''}`}
        onClick={handleItemClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="data-source-toggle">
          <div className="data-source-checkbox">
            <Checkbox
              checked={isSelected}
              onChange={() => handleSourceToggle(source.dataSourceId)}
            />
          </div>
          <div className="data-source-content">
            <div className="data-source-header">
              <div style={{ flex: '1', minWidth: '0' }}>
                <span className="data-source-title" style={{
                  display: 'block',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#232f3e',
                  lineHeight: '1.4',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}>
                  {displayName}
                </span>
              </div>
              <div className="data-source-badges">
                <Badge color={source.status === 'ACTIVE' || source.status === 'AVAILABLE' ? 'green' : 'grey'}>
                  {source.status}
                </Badge>
                <Badge color="blue">{source.type}</Badge>
                {source.knowledgeBaseName && (
                  <Badge color="purple">
                    KB: {source.knowledgeBaseName}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="data-source-metadata">
              {displayName !== source.name && (
                <div className="data-source-metadata-item">
                  <strong>Technical Name:</strong> {source.name}
                </div>
              )}
              {source.description && source.description !== source.name && displayName !== source.description && (
                <div className="data-source-metadata-item">
                  <strong>Description:</strong> {source.description}
                </div>
              )}
              <div className="data-source-metadata-item">
                <strong>Data Source ID:</strong> {source.dataSourceId}
              </div>
              {source.knowledgeBaseId && (
                <div className="data-source-metadata-item">
                  <strong>Knowledge Base ID:</strong> {source.knowledgeBaseId}
                </div>
              )}
              {source.lastSynced && (
                <div className="data-source-metadata-item">
                  <strong>Last synced:</strong> {new Date(source.lastSynced).toLocaleString()}
                </div>
              )}
            </div>
            
            {source.id === 'competitor_info' && (
              <ExpandableSection header="Competitor Information Categories">
                <SpaceBetween size="xs">
                  {COMPETITOR_INFO_CATEGORIES.map(category => (
                    <Checkbox
                      key={category.id}
                      checked={selectedCompetitorCategories.includes(category.id)}
                      onChange={() => handleCompetitorCategoryToggle(category.id)}
                    >
                      {category.label}
                    </Checkbox>
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredDataSources = dataSources;

  // Debug logging for visibility issues
  console.log('🔍 DataSourceFilter render:', {
    dataSourcesCount: dataSources.length,
    filteredCount: filteredDataSources.length,
    selectedCount: selectedSources.length,
    error: error,
    isLoading: isLoading
  });

  // Log each data source for debugging
  if (filteredDataSources.length > 0) {
    console.log('🔍 First few data sources:', filteredDataSources.slice(0, 3).map(s => ({
      name: s.name,
      id: s.id,
      status: s.status
    })));
  }

    return (
      <Container
        header={
          <Header 
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {lastRefresh && (
                  <Box color="text-body-secondary" fontSize="body-s">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                  </Box>
                )}
                <Button
                  iconName="refresh"
                  loading={isLoading}
                  onClick={handleRefresh}
                  variant="icon"
                  ariaLabel="Refresh data sources from Bedrock"
                />
              </SpaceBetween>
            }
          >
            Data Sources
            <Box variant="p" color="text-body-secondary">
              Configure which Bedrock knowledge base data sources to include
            </Box>
          </Header>
        }
      >
        {error && (
          <Alert type="warning" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        <SpaceBetween size="l">
          {/* Selection Summary */}
          {selectedSources.length > 0 && (
            <div className="selected-sources-summary">
              <div className="selected-sources-count">
                {selectedSources.length} data source{selectedSources.length !== 1 ? 's' : ''} selected
              </div>
              <Box color="text-body-secondary" fontSize="body-s">
                {selectedSources.map(source => 
                  typeof source === 'string' ? source : source.name
                ).join(', ')}
              </Box>
            </div>
          )}

          {/* Filter Controls section removed */}
  
          {/* Data Sources List */}
          <FormField
            label={`Available Sources (${filteredDataSources.length})`}
            description="Select the sources to include in your search"
          >
            <div style={{ minHeight: '100px', padding: '8px' }}>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Loading data sources...
                </div>
              ) : filteredDataSources.length > 0 ? (
                <>
                  {/* Bulk Actions */}
                  <SpaceBetween direction="horizontal" size="xs" style={{ marginBottom: '16px' }}>
                    <Button
                      variant="link"
                      onClick={() => {
                        const allFilteredSources = filteredDataSources.filter(source => 
                          !selectedSources.some(s => 
                            typeof s === 'string' ? s === source.dataSourceId : s.dataSourceId === source.dataSourceId
                          )
                        );
                        onFilterChange([...selectedSources, ...allFilteredSources]);
                      }}
                      disabled={filteredDataSources.every(source => 
                        selectedSources.some(s => 
                          typeof s === 'string' ? s === source.dataSourceId : s.dataSourceId === source.dataSourceId
                        )
                      )}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="link"
                      onClick={() => {
                        const remainingSources = selectedSources.filter(selected => 
                          !filteredDataSources.some(filtered => 
                            typeof selected === 'string' ? selected === filtered.dataSourceId : selected.dataSourceId === filtered.dataSourceId
                          )
                        );
                        onFilterChange(remainingSources);
                      }}
                      disabled={!filteredDataSources.some(source => 
                        selectedSources.some(s => 
                          typeof s === 'string' ? s === source.dataSourceId : s.dataSourceId === source.dataSourceId
                        )
                      )}
                    >
                      Clear All
                    </Button>
                  </SpaceBetween>
                  
                  <div className="data-source-filter-container">
                    {filteredDataSources.map(source => renderSourceItem(source))}
                  </div>
                </>
              ) : (
                <div className="no-sources-message">
                  {error ? `Error: ${error}` : 'No data sources found. Click refresh to try again.'}
                </div>
              )}
            </div>
          </FormField>
        </SpaceBetween>
      </Container>
    );
  };

export default DataSourceFilter;